import { RootState } from '../store';
import { entryKey, drillEntryKey } from './reconciliationsSlice';
import { RECON_ID, RECON_SCOPE, RECON_SECTION_ID } from './constants';
import type { ReconScope } from './constants';
import type { ReconDegraded, ReconSection, ReconciliationsEntry, DrillDownEntry } from './types';

export const selectReconciliationsEntry = (state: RootState, orgId: string, fys: number[] | null): ReconciliationsEntry | undefined =>
    state.reconciliations.entries[entryKey(orgId, fys)];

export const selectDrillDownEntry = (
    state: RootState,
    orgId: string,
    fys: number[] | null,
    reconId: string,
    fy: number,
): DrillDownEntry | undefined => state.reconciliations.drillDowns[drillEntryKey(orgId, fys, reconId, fy)];

export const selectOverallScore = (entry: ReconciliationsEntry | undefined) => entry?.payload?.overall ?? null;

/** Display-only filter over the one shared payload — never a second request (A-62/A-63, D6). */
export const selectSectionsForScope = (entry: ReconciliationsEntry | undefined, scope: ReconScope): ReconSection[] => {
    const sections = entry?.payload?.sections ?? [];
    if (scope === RECON_SCOPE.GST) return sections.filter((section) => section.id === RECON_SECTION_ID.REVENUE_WORKING_CAPITAL);
    if (scope === RECON_SCOPE.DEBT_DETAILS) {
        return sections
            .map((section) => ({ ...section, recons: section.recons.filter((recon) => recon.id === RECON_ID.DEBT) }))
            .filter((section) => section.recons.length > 0);
    }
    return sections;
};

/** recon id -> label, read from the FULL (unscoped) payload — D27a's `degraded.excludedReconIds`
 * names ids from the whole response, not just the recons a given scope's pill happens to show. */
export const selectReconLabelsById = (entry: ReconciliationsEntry | undefined): Record<string, string> => {
    const sections = entry?.payload?.sections ?? [];
    return Object.fromEntries(sections.flatMap((section) => section.recons.map((recon) => [recon.id, recon.label])));
};

/** `degraded` is computed over the WHOLE payload, but a scope pill can only show the recons its
 * scope holds — warning it about one it cannot show tells the underwriter nothing actionable.
 * Returns the degraded facts narrowed to this scope, or null when none of them landed here.
 * The drawer banner deliberately keeps the unscoped view (selectReconLabelsById above). */
export const selectScopedDegraded = (degraded: ReconDegraded | undefined, sections: ReconSection[]): ReconDegraded | null => {
    if (!degraded?.isDegraded) return null;
    // Empty `excludedReconIds` is the cell-level bias (A-124): a source failure cost a row its
    // worst cell without dropping any whole recon, so the payload carries no attribution to
    // intersect. Unnarrowable is not unaffected — D27a signals and never suppresses.
    if (!degraded.excludedReconIds.length) return degraded;
    const scopedReconIds = new Set(sections.flatMap((section) => section.recons.map((recon) => recon.id)));
    const excludedReconIds = degraded.excludedReconIds.filter((id) => scopedReconIds.has(id));
    return excludedReconIds.length ? { ...degraded, excludedReconIds } : null;
};
